/**
 * Central, validated access to environment variables.
 *
 * Vite exposes anything prefixed with `VITE_` on `import.meta.env`. Using it
 * directly is tempting but two things go wrong:
 *   1. Missing vars silently become `undefined`, producing confusing downstream
 *      errors ("Invalid URL: undefined") instead of a clear startup failure.
 *   2. There's no single source of truth for what envs the app actually uses,
 *      so adding a new env means hunting through the codebase.
 *
 * Build modes and their files (Vite convention):
 *   `vite`                         → .env, .env.development, .env.development.local
 *   `vite build --mode staging`    → .env, .env.staging, .env.staging.local
 *   `vite build`                   → .env, .env.production, .env.production.local
 *
 * Add a new var in exactly two places: this file and `.env.example`.
 *
 * Testing note: `env` is a `const` snapshot of `import.meta.env` at module-
 * load time. Tests that need to exercise different env combinations must:
 *
 *   1. `vi.stubEnv('VITE_FOO', 'value')` BEFORE the first import, and
 *   2. `vi.resetModules()` + `await import('../env')` to pick up the stub.
 *
 * Setting `import.meta.env.VITE_FOO = 'x'` directly will NOT work after the
 * module has been imported once — the `required`/`optional` readers above
 * run at import time and the exported `env` object is frozen immediately
 * after. The same pattern is used by extension-version.test.ts — see there
 * for a worked example with `vi.doMock`.
 */

export type AppEnv = 'development' | 'staging' | 'production'

const PROD_SUPABASE_REF = 'gcaydyucdduztdugrrgy'

function required(name: string): string {
  const value = import.meta.env[name]
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(
      `[env] Missing required environment variable: ${name}. ` +
      `Check your .env file — see .env.example for the full list.`
    )
  }
  return value
}

function optional(name: string): string | undefined {
  const value = import.meta.env[name]
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

/**
 * Environment name — one of 'development' | 'staging' | 'production'.
 * Defaults to Vite's MODE when VITE_APP_ENV is not set.
 */
function resolveAppEnv(): AppEnv {
  const explicit = optional('VITE_APP_ENV')
  if (explicit === 'development' || explicit === 'staging' || explicit === 'production') {
    return explicit
  }
  // Fallback to Vite's MODE (dev / production / custom).
  const mode = import.meta.env.MODE
  if (mode === 'production') return 'production'
  if (mode === 'staging') return 'staging'
  return 'development'
}

// `appEnv` never changes at runtime, so the `isDev` / `isStaging` / `isProd`
// booleans are eagerly evaluated instead of exposed as getters. Getters force
// every callsite to re-read `this.appEnv` on each access and were only there
// because the original draft had `appEnv` mutable — it isn't anymore.
const _appEnv = resolveAppEnv()
const _supabaseUrl = required('VITE_SUPABASE_URL')
const _supabaseAnonKey = required('VITE_SUPABASE_ANON_KEY')

if (_appEnv !== 'production' && _supabaseUrl.includes(PROD_SUPABASE_REF)) {
  throw new Error(
    '[env] Refusing to start a non-production build against production Supabase. ' +
    'Use .env.development / .env.staging for local or staging work.'
  )
}

export const env = {
  /** High-level environment label. Use this to gate features / show dev badges. */
  appEnv: _appEnv,
  /** True in dev builds only. Convenient for `if (env.isDev) log.debug(...)`. */
  isDev: _appEnv === 'development',
  /** True in staging builds — useful for opt-in beta features. */
  isStaging: _appEnv === 'staging',
  /** True in production builds — gate analytics / Sentry here. */
  isProd: _appEnv === 'production',

  supabase: {
    url: _supabaseUrl,
    anonKey: _supabaseAnonKey,
  },

  /** Optional — set in staging/prod. Used by future Sentry plumbing. */
  sentryDsn: optional('VITE_SENTRY_DSN'),
  /** Optional — set in staging/prod. Used by future PostHog plumbing. */
  posthogKey: optional('VITE_POSTHOG_KEY'),
} as const

export type Env = typeof env
